/**
 * Cart Drawer — shared slide-in panel controller.
 * Exposes window.AldarCartDrawer = { open, close, toggle, refresh } for the PDP
 * scripts (aldar-product.js, lens.js) to call after a successful Add to Cart.
 * Reuses window.AldarCart (already defined in theme.js) for all cart AJAX when
 * present; falls back to raw /cart.js /cart/change.js fetches otherwise.
 * Also opens on click of the existing header cart icon (a.ha-item containing
 * [data-cart-count]) — read-only DOM query, no existing file is modified.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  var overlay = document.querySelector('[data-cart-drawer-overlay]');
  if (!drawer || !overlay) return;

  var itemsEl = drawer.querySelector('[data-cart-drawer-items]');
  var emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
  var footerEl = drawer.querySelector('[data-cart-drawer-footer]');
  var countEl = drawer.querySelector('[data-cart-drawer-count]');
  var totalEl = drawer.querySelector('[data-cart-drawer-total]');
  var deliveryEl = drawer.querySelector('[data-cart-drawer-delivery]');
  var deliveryTextEl = drawer.querySelector('[data-cart-drawer-delivery-text]');
  var closeBtn = drawer.querySelector('[data-cart-drawer-close]');

  var deliveryText = drawer.getAttribute('data-delivery-text') || '';

  var moneyFormat = document.body.getAttribute('data-money-format') || 'QAR {{amount}}';

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var negative = cents < 0;
    var value = Math.abs(cents) / 100;
    var withDecimals = value.toFixed(2);
    var decimalParts = withDecimals.split('.');
    var noDecimals = Math.round(value).toString();

    function commaSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function spaceSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }
    var replacements = {
      amount: withDecimals,
      amount_no_decimals: noDecimals,
      amount_with_comma_separator: commaSeparate(decimalParts[0]) + '.' + decimalParts[1],
      amount_no_decimals_with_comma_separator: commaSeparate(noDecimals),
      amount_with_space_separator: spaceSeparate(decimalParts[0]) + ',' + decimalParts[1],
      amount_no_decimals_with_space_separator: spaceSeparate(noDecimals),
      amount_with_period_and_space_separator: spaceSeparate(decimalParts[0]) + '.' + decimalParts[1]
    };
    var result = moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match;
    });
    return negative ? '-' + result : result;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* =====================================================================
     CART AJAX (reuses window.AldarCart when available)
     ===================================================================== */
  function cartState() {
    if (window.AldarCart && typeof window.AldarCart.state === 'function') {
      return window.AldarCart.state();
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function cartChange(line, quantity) {
    if (window.AldarCart && typeof window.AldarCart.change === 'function') {
      return window.AldarCart.change(line, quantity);
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }
  function refreshBadge() {
    if (window.AldarCart && typeof window.AldarCart.refreshBadge === 'function') {
      window.AldarCart.refreshBadge();
    }
  }

  /* =====================================================================
     RENDER
     ===================================================================== */
  function propertyEntries(properties) {
    if (!properties) return [];
    var entries = [];
    Object.keys(properties).forEach(function (key) {
      if (key.charAt(0) === '_') return; // Shopify convention: hidden/internal properties
      var value = properties[key];
      if (value === null || value === undefined || value === '') return;
      entries.push([key, value]);
    });
    return entries;
  }

  function renderLine(item, lineIndex) {
    var entries = propertyEntries(item.properties);
    var hasRx = entries.length > 0;
    var onSale = item.original_price && item.original_price > item.final_price;

    var rxHtml = '';
    if (hasRx) {
      var rows = entries.map(function (pair) {
        return '<div><strong>' + escapeHtml(pair[0]) + ':</strong> ' + escapeHtml(pair[1]) + '</div>';
      }).join('');
      rxHtml =
        '<div class="cart-drawer-rx" data-cart-drawer-rx>' +
          '<button type="button" class="cart-drawer-rx-trigger" data-cart-drawer-rx-trigger aria-expanded="false">' +
            '<span>Lenses and prescription</span>' +
            '<svg class="cart-drawer-rx-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>' +
          '</button>' +
          '<div class="cart-drawer-rx-panel" data-cart-drawer-rx-panel hidden>' + rows + '</div>' +
        '</div>';
    }

    var variantLine = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<div class="cart-drawer-line-variant">' + escapeHtml(item.variant_title) + '</div>'
      : '';

    var el = document.createElement('div');
    el.className = 'cart-drawer-line';
    el.setAttribute('data-cart-drawer-line', '');
    el.setAttribute('data-line-index', lineIndex);
    el.innerHTML =
      '<div class="cart-drawer-line-badge">' +
        (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">' : '') +
      '</div>' +
      '<div class="cart-drawer-line-body">' +
        '<div class="cart-drawer-line-title">' + escapeHtml(item.product_title) + '</div>' +
        variantLine +
        '<div class="cart-drawer-line-price">' +
          formatMoney(item.final_price) +
          (onSale ? '<span class="was">' + formatMoney(item.original_price) + '</span>' : '') +
        '</div>' +
        rxHtml +
        '<div class="cart-drawer-line-row">' +
          '<div class="qty-stepper" data-cart-drawer-qty>' +
            '<button type="button" data-cart-drawer-qty-minus aria-label="Decrease quantity">\u2212</button>' +
            '<span data-cart-drawer-qty-value>' + item.quantity + '</span>' +
            '<button type="button" data-cart-drawer-qty-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" class="cart-drawer-remove" data-cart-drawer-remove>Remove</button>' +
        '</div>' +
      '</div>';
    return el;
  }

  function render(cart) {
    itemsEl.querySelectorAll('[data-cart-drawer-line]').forEach(function (el) { el.remove(); });

    if (!cart.items || cart.items.length === 0) {
      emptyEl.hidden = false;
      footerEl.hidden = true;
      if (deliveryEl) deliveryEl.hidden = true;
    } else {
      emptyEl.hidden = true;
      footerEl.hidden = false;
      if (deliveryEl && deliveryText) {
        deliveryEl.hidden = false;
        if (deliveryTextEl) deliveryTextEl.textContent = deliveryText;
      }
      cart.items.forEach(function (item, i) {
        itemsEl.appendChild(renderLine(item, i + 1));
      });
    }

    if (countEl) countEl.textContent = '(' + cart.item_count + (cart.item_count === 1 ? ' Item)' : ' Items)');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
  }

  function refresh() {
    return cartState().then(function (cart) {
      render(cart);
      return cart;
    });
  }

  /* =====================================================================
     OPEN / CLOSE
     ===================================================================== */
  function open() {
    overlay.hidden = false;
    requestAnimationFrame(function () {
      overlay.classList.add('visible');
      drawer.classList.add('open');
    });
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function close() {
    overlay.classList.remove('visible');
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { overlay.hidden = true; }, 300);
  }
  function toggle() {
    if (drawer.classList.contains('open')) close(); else open();
  }

  if (closeBtn) closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('open')) close();
  });

  /* =====================================================================
     LINE ITEM INTERACTIONS (qty +/-, remove, prescription toggle)
     ===================================================================== */
  itemsEl.addEventListener('click', function (e) {
    var line = e.target.closest('[data-cart-drawer-line]');
    if (!line) return;
    var lineIndex = parseInt(line.getAttribute('data-line-index'), 10);

    var rxTrigger = e.target.closest('[data-cart-drawer-rx-trigger]');
    if (rxTrigger) {
      var expanded = rxTrigger.getAttribute('aria-expanded') === 'true';
      var panel = rxTrigger.parentElement.querySelector('[data-cart-drawer-rx-panel]');
      rxTrigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (panel) panel.hidden = expanded;
      return;
    }

    var minus = e.target.closest('[data-cart-drawer-qty-minus]');
    var plus = e.target.closest('[data-cart-drawer-qty-plus]');
    var remove = e.target.closest('[data-cart-drawer-remove]');
    var qtyValueEl = line.querySelector('[data-cart-drawer-qty-value]');

    if (remove) {
      cartChange(lineIndex, 0).then(function (cart) { render(cart); refreshBadge(); });
      return;
    }
    if (minus || plus) {
      var current = parseInt(qtyValueEl.textContent, 10) || 1;
      var next = plus ? current + 1 : Math.max(0, current - 1);
      cartChange(lineIndex, next).then(function (cart) { render(cart); refreshBadge(); });
    }
  });

  /* =====================================================================
     PUBLIC API
     ===================================================================== */
  window.AldarCartDrawer = { open: open, close: close, toggle: toggle, refresh: refresh };

  /* =====================================================================
     Header cart icon opens the drawer instead of navigating to /cart
     (read-only query against existing header markup — no file changes).
     ===================================================================== */
  Array.prototype.slice.call(document.querySelectorAll('a.ha-item')).forEach(function (link) {
    if (!link.querySelector('[data-cart-count]')) return;
    link.addEventListener('click', function (e) {
      e.preventDefault();
      refresh().then(open);
    });
  });

  refresh();
})();
